<div
  class="cbsw-booking-summary-step"
  #bookingSummaryView
  role="region"
  [attr.aria-label]="'Booking summary'"
  [attr.aria-live]="'polite'"
  [attr.aria-busy]="!bookingService.selectedBookingData?.cartItems?.length"
>
  @if (bookingService.selectedBookingData?.cartItems?.length > 0) {
    <div class="mb-2" role="group" [attr.aria-label]="'Booking summary header'">
      <app-csw-text
        [text]="templateTitle | translation: 'template_content': lang"
        customClass="step-element-title cbsw-booking-summary-step-title fw-600"
        role="heading"
        aria-level="2"
      />
      <div class="text-muted cbsw-booking-summary-step-subtitle" role="text">
        {{ templateSubTitle | translation: 'template_content': lang }}
      </div>
    </div>
    <!-- Cart Items Starts -->
    <div class="cbsw-cart-container mb-3 mt-3">
    <ul
      class="list-group cbsw-cart-list"
      role="list"
      [attr.aria-label]="'Selected services'"
    >
      @for (cartItem of bookingService.selectedBookingData.cartItems; track cartItem; let index = $index) {
        <li
          class="list-group-item d-flex justify-content-between lh-condensed"
          role="listitem"
          [attr.aria-label]="'Service ' + (index + 1) + ' of ' + bookingService.selectedBookingData.cartItems.length"
        >
          <div role="group" [attr.aria-label]="'Service details'">
            <div
              class="my-0 list-item-title cbsw-booking-service-name"
              role="heading"
              aria-level="3"
              [innerHTML]="(cartItem?.service | translation: 'name' : lang) | calioSafe: 'html'"
            ></div>
            <div
              class="my-0 list-item-title cbsw-booking-service-description"
              role="text"
              [innerHTML]="(cartItem?.service | translation: 'description' : lang) | calioSafe: 'html'"
            ></div>
            <div
              class="text-muted description cbw-summary-date-time"
              role="text"
              [attr.aria-label]="'Appointment date and time'"
            >
              {{ cartItem.date | date: 'dd.MM.y' }}
              , {{ cartItem.startTime | dateutc: timeFormat }} - {{ cartItem.endTime | dateutc: timeFormat }}
            </div>
            <div
              class="description text-muted cbw-summary-resource-name"
              role="text"
              [attr.aria-label]="'Selected worker'"
            >
              {{ cartItem?.worker?.resource_name }}
            </div>
            @if (cartItem.selectedMeetingTypeId || bookingService.selectedBookingData.selectedUser?.meeting_type_id) {
              @if ((bookingService.widgetTemplates | calioMeetingTemplate: {
                id: cartItem.selectedMeetingTypeId || bookingService.selectedBookingData.selectedUser?.meeting_type_id,
                type: 'meeting_template' }); as widgetTemplate
              ) {
                <div
                  class="description text-muted cbw-summary-meeting-type"
                  role="text"
                  [attr.aria-label]="'Meeting type'"
                  [innerHTML]="(widgetTemplate.titleTemplate | translation: 'template_content' : lang) | calioSafe: 'html'"
                ></div>
              }
              @if (cartItem.selectedMeetingTypeId === 1 || bookingService.selectedBookingData.selectedUser?.meeting_type_id === 1) {
                <ng-container [ngTemplateOutlet]="inStoreMeetingTypeLocation" />
              }
            } @else {
              @if ((bookingService.widgetTemplates | calioMeetingTemplate: { id: meetingTypeId.STORE, type: 'meeting_template' }); as widgetTemplate) {
                <div
                  class="description text-muted cbw-summary-meeting-type"
                  role="text"
                  [attr.aria-label]="'Meeting type'"
                  [innerHTML]="(widgetTemplate.titleTemplate | translation: 'template_content' : lang) | calioSafe: 'html'"
                ></div>
              }
              <ng-container [ngTemplateOutlet]="inStoreMeetingTypeLocation" />
            }
            <ng-template #inStoreMeetingTypeLocation>
              <div
                class="cbw-summary-worker-store-location"
                role="group"
                [attr.aria-label]="'Store location'"
              >
                <span
                  class="cbw-summary-worker-store-location-name"
                  role="text"
                >{{ cartItem.store | translation: 'name' : lang }}</span>
                @if (cartItem.store?.is_online === 0) {
                  <span class="cbw-summary-worker-store-location-separator" aria-hidden="true">,&nbsp;</span>
                  <span
                    class="cbw-summary-worker-store-location-address whitespace-pre-line"
                    role="text"
                  >
                    @let addressInput = {
                      street: cartItem.store.street,
                      zip: cartItem.store.zip,
                      city: cartItem.store.city,
                      country: cartItem.store.country | translation: 'name' : lang,
                      country_code: cartItem.store.country?.identifier,
                    };
                    {{ addressInput | addressFormat: 'single' }}
                  </span>
                }
              </div>
            </ng-template>
          </div>
          <div
            class="d-flex flex-column align-items-end justify-content-between"
            role="group"
            [attr.aria-label]="'Service price and actions'"
          >
            <div
              class="text-muted text-end"
              role="text"
              [attr.aria-label]="'Service price'"
            >
              @if (cartItem?.service?.price === 0) {
                {{ 'booking_components.free' | translate }}
              } @else if (cartItem?.service?.price === -1) {
                &nbsp;
              } @else if (cartItem?.service?.price > 0) {
                {{ cartItem.service.price | calioCurrency: bookingService.partnerData?.currency: lang }}
              }
              @if (
                cartItem.service?.tax && 
                cartItem.service?.tax?.rate >= 0 &&
                cartItem?.service?.price > 0
              ) {
                <div
                  class="cbsw-taxes"
                  role="text"
                  [attr.aria-label]="'Tax information'"
                >
                  @if (isVatAddedOnTop(bookingService.partnerData?.widget_vat_type)) {
                    <span class="cbsw-taxes-amount">+ {{ itemVatAmount(cartItem) | calioCurrency: bookingService.partnerData?.currency: lang }}</span>
                  }
                  ({{ 'booking_components.tax_info.' + vatTypeLabelKey(bookingService.partnerData?.widget_vat_type) | translate }}
                    @if (bookingService.partnerData?.widget_vat_type !== vatType.EXCLUDE) {
                      <span class="cbsw-taxes-percentage">{{ cartItem.service.tax.rate }}%</span>
                    }
                    {{ 'booking_components.tax_info.taxes' | translate }})
                </div>
              }
            </div>
            @if (!paymentAttemptLocked) {
              <div
                class="text-danger remove-button"
                (click)="removeCartItem(index)"
                role="button"
                [attr.aria-label]="'Remove ' + (cartItem?.service | translation: 'name' : lang) + ' from cart'"
                tabindex="0"
              >
                {{ 'booking_components.remove' | translate }}
              </div>
            }
          </div>
        </li>
      }
      @if (subTotal > 0 && (bookingService.partnerData?.widget_vat_type !== vatType.EXCLUDE || showCoupon)) {
        <li
          class="list-group-item d-flex justify-content-between lh-condensed"
          role="listitem"
          [attr.aria-label]="'Subtotal'"
        >
          <div role="group">
            <div
              class="my-0 list-item-title"
              role="text"
            > {{ subtotalLabelKey() | translate }}</div>
          </div>
          <div role="text">
            <span class="text-muted">
              @if (subTotal === 0) {
                {{ 'booking_components.free' | translate }}
              }
              @if (subTotal === -1) {
                &nbsp;
              }
              @if (subTotal > 0) {
                {{ subTotal | calioCurrency: bookingService.partnerData?.currency: lang }}
              }
            </span>
          </div>
        </li>
      }
      @if (showCoupon) {
        <li
          class="list-group-item d-flex justify-content-between bg-light"
          role="listitem"
          [attr.aria-label]="'Coupon discount'"
        >
          <div class="text-success" role="group">
            <div
              class="my-0 list-item-title"
              role="text"
            >
              {{ 'coupon_credit_card_info.coupon' | translate }}
            </div>
            <div
              class="description"
              role="text"
              [attr.aria-label]="'Coupon code'"
            >
              {{ couponCode }}
            </div>
          </div>
          <div
            class="d-flex flex-column align-items-end justify-content-between"
            role="group"
          >
            <div
              class="text-success"
              role="text"
              [attr.aria-label]="'Coupon discount amount'"
            >
              -{{ finalCouponDiscount | calioCurrency: bookingService.partnerData?.currency: lang }}
            </div>
            @if (!paymentAttemptLocked) {
              <div
                class="text-danger remove-button"
                (click)="removeCoupon()"
                role="button"
                [attr.aria-label]="'Remove coupon'"
                tabindex="0"
              >
                {{ 'booking_components.remove' | translate }}
              </div>
            }
          </div>
        </li>
      }
      @for (rateEntry of vat().byRate; track rateEntry.rate) {
        <li
          class="list-group-item d-flex justify-content-between lh-condensed"
          role="listitem"
          [attr.aria-label]="'VAT'"
        >
          <div role="group">
            <div
              class="my-0 list-item-title"
              role="text"
            >{{ 'booking_components.tax_info.tax_line' | translate: { percentage: rateEntry.rate } }}</div>
          </div>
          <div role="text">
            <span class="text-muted">
              + {{ rateEntry.amountCents / 100 | calioCurrency: bookingService.partnerData?.currency: lang }}
            </span>
          </div>
        </li>
      }
      @for (rateEntry of includedVat().byRate; track rateEntry.rate) {
        <li
          class="list-group-item d-flex justify-content-between lh-condensed"
          role="listitem"
          [attr.aria-label]="'VAT'"
        >
          <div role="group">
            <div
              class="my-0 list-item-title"
              role="text"
            >{{ 'booking_components.tax_info.tax_included_line' | translate: { percentage: rateEntry.rate } }}</div>
          </div>
          <div role="text">
            <span class="text-muted">
              {{ rateEntry.amountCents / 100 | calioCurrency: bookingService.partnerData?.currency: lang }}
            </span>
          </div>
        </li>
      }
      @if (totalAmount > 0 || showCoupon) {
        <li
          class="list-group-item d-flex justify-content-between"
          role="listitem"
          [attr.aria-label]="'Total amount'"
        >
          <div role="group">
            <div
              class="my-0 list-item-title"
              role="text"
            >{{ totalLabelKey() | translate }}</div>
          </div>
          <strong role="text">
          {{ totalAmount | calioCurrency: bookingService.partnerData?.currency: lang }}
          </strong>
        </li>
      }
    </ul>
    <!-- Related Service Suggestions -->
    @if (bookingService.partnerData?.is_cart_supported == 1 && !cartFrozen) {
      <app-related-service-suggestions
        class="cbsw-related-services-wrapper"
        (addRelatedServiceEvent)="onAddRelatedService($event)"
      />
    }
    </div>
    <!-- The one booking-error banner: next to the payment form, where every refused payment lands. -->
    @if (bookingErrorMessage(); as message) {
      <div
        class="alert alert-danger mt-2 mb-3"
        role="alert"
        aria-live="assertive"
        id="booking-error-message"
      >
        <div role="text">{{ message }}</div>
      </div>
    }
    @if (technicalRefundIds(); as ids) {
      <div class="alert alert-info mb-3" role="status">
        {{ 'payment_components.refundNotice' | translate: { transactionId: ids } }}
      </div>
    }
    @if (refusedRefundIds(); as ids) {
      <div class="alert alert-info mb-3" role="status">
        {{ refundNoticeRefusedKey | translate: { transactionId: ids } }}
      </div>
    }
    @if (unconfirmedSessionIds(); as ids) {
      <div class="alert alert-warning mb-3" role="status">
        {{ bookingOutcomeUnknownNoticeKey | translate: { transactionId: ids } }}
      </div>
    }
    @if (spentSessionIds(); as ids) {
      <div class="alert alert-info mb-3" role="status">
        {{ paymentSessionSpentNoticeKey | translate: { transactionId: ids } }}
      </div>
    }
    @if (adyenAlreadyBooked()) {
      <!-- The payment already backs a booking: nothing on this summary may pay or book again. -->
      <div class="alert alert-info mb-4 cbsw-adyen-already-booked" role="status" aria-live="polite">
        {{ adyenAlreadyBookedKey | translate }}
      </div>
    } @else if (adyenReplaying()) {
      <!-- The /book sent before the page died is being sent again: only its answer may say booked. -->
      <div class="alert alert-info mb-4 cbsw-adyen-checking" role="status" aria-live="polite">
        {{ adyenCheckingKey | translate }}
      </div>
    } @else if (adyenBookingFailure() === 'completing') {
      <!-- The payment is taken and its booking is being completed: nothing here may pay again. -->
      <div class="alert alert-info mb-4 cbsw-adyen-completing" role="status" aria-live="polite">
        {{ bookingCompletingNoticeKey | translate: { transactionId: completingSessionIds() } }}
      </div>
    } @else if (payAgainRequired()) {
      <!-- A booking may exist: the payment form stays down until the customer asks for it. -->
      <div class="d-flex justify-content-end mb-4">
        <app-button
          id="{{ 'booking-summary-pay-again-btn' }}"
          (clickEvent)="payAgain()"
          [fontColor]="widgetService?.widgetConf?.button_font_color"
          [backgroundColor]="widgetService?.widgetConf?.button_background_color"
        >
          {{ 'payment_components.payAgain' | translate }}
        </app-button>
      </div>
    } @else if (adyenBookingFailure() === 'refused') {
      <!-- The backend refused this slot: paying again for it only earns the same answer. -->
      <div class="d-flex justify-content-end mb-4">
        <app-button
          id="{{ 'booking-summary-choose-another-time-btn' }}"
          (clickEvent)="chooseAnotherTime()"
          [fontColor]="widgetService?.widgetConf?.button_font_color"
          [backgroundColor]="widgetService?.widgetConf?.button_background_color"
        >
          {{ 'payment_components.chooseAnotherTime' | translate }}
        </app-button>
      </div>
    } @else {
      <app-payment-method-view
        [coupon]="bookingService.selectedBookingData.selectedCoupon"
        [finalCouponDiscount]="finalCouponDiscount"
        [originalAmount]="subTotal"
        [priceAfterCouponReduction]="finalCouponDiscount"
        [storageComplete]="storageComplete"
        (previousPageEvent)="previous()"
        (onAdditionalServiceEvent)="onAdditionalService()"
        role="complementary"
        [attr.aria-label]="'Payment method selection'"
      />
    }

  }
  @if (bookingService.selectedBookingData?.cartItems?.length === 0) {
    <div
      class="alert alert-danger"
      role="alert"
      aria-live="assertive"
      [attr.aria-label]="'Empty cart warning'"
    >
      <div role="text">
        {{ 'booking_components.emptyCartWarning' | translate }}
      </div>
    </div>
    @if (bookingService.partnerData.is_cart_supported === 1) {
      <div
        class="d-flex justify-content-center mt-4 mb-4"
        role="group"
        [attr.aria-label]="'Add service action'"
      >
        <div>
          <app-button
            id="{{ 'booking-summary-additional-service-btn' }}"
            (clickEvent)="onAdditionalService()"
            [fontColor]="widgetService?.widgetConf?.button_font_color"
            [backgroundColor]="widgetService?.widgetConf?.button_background_color"
            role="button"
            [attr.aria-label]="'Add another service'"
            tabindex="0"
          >
            {{ 'booking_components.add_service' | translate }}
          </app-button>
        </div>
      </div>
    }
  }
</div>
